import React from "react";
import SvgIcon from "../../../../../assets/icons/SvgIcon";
import { Link } from "react-router-dom";
import {
  ageFormatDateDifferenceFn,
  dateTimeFormat,
  downloadFn,
  formatPrice,
  getLocalStorage,
} from "../../../../../utils";
import { LOCAL_STORAGE_KEYS } from "../../../../../constants";
import SizeConverter from "../../../../../components/SizeConverter";

export interface ProjectDetailSectionProps {
  projectData: any;
  enquiryCreate: () => void;
  authModel: () => void;
  filterUniqueBuildingTypes: (data: any, maxCount?: number) => any[];
}

const ProjectDetailSection: React.FC<ProjectDetailSectionProps> = ({
  projectData,
  authModel,
  enquiryCreate,
  filterUniqueBuildingTypes,
}) => {
  const featureDataList = [
    {
      icon: "sq-ft-icon",
      title: "Project Area",
      value: <SizeConverter baseSize={projectData?.project_area} />,
      isActive: !!projectData?.project_area,
    },
    {
      icon: "building-icon",
      title: `Total ${projectData?.project_type === 1 ? "Tower /" : ""} Units`,
      value: `${projectData?.project_type === 1 ? `${projectData?.total_towers} /` : ""}  ${projectData?.total_houses ?? projectData?.total_flats} `,
      isActive:
        !!projectData?.total_towers ||
        (projectData?.total_houses ?? projectData?.total_flats),
    },
    {
      icon: "buildings-icon",
      title: "Project Story",
      value: `${projectData?.total_stories}`,
      isActive: !!projectData?.total_stories,
    },
  ];

  const authToken = getLocalStorage(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);

  return (
    <div className="xs:p-4 py-3 xs:py-6 project-gray-section">
      <div className="gap-6 md:gap-10 grid grid-cols-1 md:grid-cols-2 w-full">
        <div className="flex items-start md:items-center gap-3 bg-white shadow shadow-black/10 p-2 md:px-4 border rounded-xl">
          <div className="bg-gray-400/20 p-2 rounded-full">
            <SvgIcon iconName="pin-location-icon" className="size-4" />
          </div>
          <p className="font-normal text-blue-500 hover:text-blue-800 xs:text-lg">
            <Link
              to={`https://www.google.com/maps?q=${projectData?.lat},${projectData?.long}`}
              target="_blank"
            >
              {projectData?.location}
            </Link>
          </p>
        </div>
        <div className="space-y-6 w-full">
          <div className="flex flex-wrap justify-start items-center gap-4 xs:gap-6">
            <div className="flex justify-start items-center gap-x-2">
              <div className="bg-dark-cyan p-1.5 rounded-full">
                <SvgIcon
                  iconName="arrow-down-full"
                  className="size-3 xs:size-4"
                />
              </div>

              <button
                onClick={() => {
                  if (authToken) {
                    enquiryCreate();
                    downloadFn(projectData?.brochure);
                  } else {
                    authModel();
                  }
                }}
                className="border-b border-b-black font-semibold xs:font-bold"
              >
                <h6 className="text-black xs:text-lg">Download Brochure</h6>
              </button>
            </div>
            {[
              {
                iconName: "launch-calendar-icon",
                label: "Launch Date",
                value: dateTimeFormat(projectData?.launch_date, "MMM YYYY"),
                isHide: projectData?.launch_date,
              },
              {
                iconName: "possession-icon",
                label: "Age of Project",
                value: ageFormatDateDifferenceFn(projectData?.age_of_project),
                isHide: projectData?.age_of_project,
              },
              {
                iconName: "possession-icon",
                label: "Possession",
                value: dateTimeFormat(projectData?.possession_date, "MMM YYYY"),
                isHide: projectData?.possession_date,
              },
            ].map((item, index) => {
              if (item.isHide) {
                return (
                  <div
                    key={index}
                    className="flex justify-start items-center gap-x-2"
                  >
                    <SvgIcon iconName={item.iconName} className="size-5" />
                    <h6 className="font-semibold xs:font-bold text-black xs:text-lg">
                      {item.label}:{" "}
                      <span className="font-normal text-black">
                        {item.value}
                      </span>
                    </h6>
                  </div>
                );
              }
            })}
            {/* RERA NUMBER */}
            {projectData?.rera_number ? (
              <div className="space-y-3">
                <div className="flex justify-start items-center gap-2">
                  <div>
                    <SvgIcon iconName="rera-icon" className="size-6" />
                  </div>
                  <div className="flex flex-row gap-2 font-semibold xs:font-bold text-black xs:text-lg">
                    <h2 className="min-w-fit leading-8">Rera Details:</h2>
                    <Link
                      to={`https://gujrera.gujarat.gov.in/#/project-preview`}
                      target="_blank"
                      className="max-w-xl font-medium text-blue-600 text-lg break-all line-clamp-1"
                    >
                      {projectData?.rera_number}
                    </Link>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="justify-start items-center gap-2 xs:gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 xl:grid-cols-4 mt-4">
        {/* FEATURES LIST */}
        {featureDataList
          .filter((item: any) => item?.isActive)
          .map((item: any) => (
            <div
              key={item?.icon}
              className="space-y-2 bg-white shadow shadow-black/10 p-2 xs:p-4 border rounded-md w-full"
            >
              <h3 className="font-medium xs:font-semibold text-black text-base">
                {item?.title}
              </h3>
              <div className="flex justify-start items-center gap-x-2 xs:gap-x-3">
                <SvgIcon iconName={item?.icon} className="size-5" />
                <h6 className="font-normal text-black/80 text-sm xs:text-base">
                  {item?.value}
                </h6>
              </div>
            </div>
          ))}
        {/* BUILDING LIST */}
        {projectData?.buildings?.length ? (
          <>
            {filterUniqueBuildingTypes(projectData?.buildings, 8)?.map(
              (item: any) => (
                <div
                  key={item?._id}
                  className="space-y-2 bg-white shadow shadow-black/10 p-2 xs:p-4 border rounded-md w-full"
                >
                  <div className="gap-3 grid grid-cols-12">
                    <h3 className="col-span-8 font-medium xs:font-semibold text-black text-base break-all line-clamp-1">
                      {item?.building_type === 0
                        ? "Commercial "
                        : `${item?.building_type} BHK `}
                      {item?.sub_category}
                    </h3>
                    <h4 className="col-span-4 text-right">
                      {item?.building_expected_price ? (
                        <span className="text-sm xs:text-base">
                          ₹{formatPrice(item?.building_expected_price)}
                        </span>
                      ) : null}
                    </h4>
                  </div>
                  <div className="flex justify-start items-center gap-x-2 xs:gap-x-3">
                    <SvgIcon iconName="building-icon" className="size-6" />
                    <div className="flex justify-start items-center gap-2 w-full font-normal text-black/80 text-base">
                      <SizeConverter
                        baseSize={
                          item?.min_outer !== item?.max_outer
                            ? [item?.min_outer, item?.max_outer]
                            : item?.min_outer
                        }
                      />
                      <h6 className="text-sm xs:text-base line-clamp-1">SBA</h6>
                    </div>
                  </div>
                </div>
              )
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ProjectDetailSection;
